import { Component, EventEmitter, forwardRef, Inject, Input, NgZone, OnInit, Output } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { SOCKET_TOPIC_EXPORT, SOCKET_TOPIC_EXPORT_PAYMENTS, SOCKET_TOPIC_EXPORT_REPORT, SOCKET_TOPIC_EXPORT_TROUBLESHOOTING } from '../../common/endpoints';
import { DownloadingObject, DownloadService } from '../../service/download.service';
import { HttpService } from '../../service/http.service';
import { SocketService } from '../../service/socket.service';
import { HttpUtility } from '../../utility';
import { fadeOutAnimation } from './download-dialog.animation';
import { DownloadStatus, DownloadType } from './download-dialog.component';

// Hybrid PDF completion: under heavy dashboard load SockJS can die silently while STOMP still reports
// connected, so DONE may be lost. Keep STOMP as the fast path and poll REST as a fallback.
const PDF_POLL_START_DELAY_MS = 20000;
const PDF_POLL_INTERVAL_MS = 5000;
const PDF_FAIL_DEADLINE_MS = 180000;


@Component({
    selector: 'download-element',
    template: require('./download-element.component.html'),
    styles: [require('./download-element.component.css')],
    animations: [fadeOutAnimation]
})

export class DownloadElementComponent implements OnInit {

    @Input() element: DownloadingObject;

    @Output() downloadCompleted = new EventEmitter();

    @Output() elementAdded = new EventEmitter();

    private socketSubscriptionId: number;
    private downloadStarted: boolean = false;

    private pdfDownloadSucceeded: boolean = false;
    private pdfDownloadInProgress: boolean = false;
    private pdfPollStartTimeoutId: any;
    private pdfPollIntervalId: any;
    private pdfStartedAt: number;

    fadeOut: boolean;

    constructor(
        @Inject(forwardRef(() => SocketService)) private socketService: SocketService,
        @Inject(forwardRef(() => DownloadService)) private downloadService: DownloadService,
        @Inject(forwardRef(() => NgZone)) private zone: NgZone,
        @Inject(forwardRef(() => HttpService)) private httpService: HttpService,
        @Inject(forwardRef(() => HttpUtility)) private httpUtility: HttpUtility
    ) { }

    ngOnInit() {
        if (this.element.type == DownloadType.CSV) {
            this.elementAdded.emit();
            this.handleCsvDownload();
        } else if (this.element.type == DownloadType.CUSTOM) {
            this.handleCustomDownload();
        } else {
            let topic;
            switch (this.element.type) {
                case "XLSX_METRICS":
                case "ZIP_METRICS":
                    topic = SOCKET_TOPIC_EXPORT;
                    break;
                case "PDF":
                    topic = SOCKET_TOPIC_EXPORT_REPORT;
                    break;
                case "XLSX_TROUBLESHOOTING":
                    topic = SOCKET_TOPIC_EXPORT_TROUBLESHOOTING;
                    break;
                case "CSV_PAYMENTS":
                    topic = SOCKET_TOPIC_EXPORT_PAYMENTS;
                    break;
            }
            let subscriber = {
                topic: topic.replace('{uuid}', this.element.uuid),
                callback: message => this.handleExportMessage(message)
            };
            this.elementAdded.emit();
            this.socketSubscriptionId = this.socketService.subscribe(subscriber);
            if (this.element.type == DownloadType.PDF) {
                this.pdfStartedAt = Date.now();
                this.pdfPollStartTimeoutId = setTimeout(() => this.startPdfPolling(), PDF_POLL_START_DELAY_MS);
            }
        }
    }

    setHoverOption(): void {
        if (this.element.status != DownloadStatus.DOWNLOADED && this.element.status != DownloadStatus.ERROR) {
            this.element.status = DownloadStatus.CANCEL;
        }
    }

    setLeaveOption(): void {
        if (this.element.status != DownloadStatus.DOWNLOADED && this.element.status != DownloadStatus.ERROR) {
            this.element.status = DownloadStatus.DOWNLOADING;
        }
    }

    ngOnDestroy() {
        this.cleanupSocketSubscription();
        this.stopPdfTimers();
    }

    handleExportMessage(message: any): void {
        const data = this.parseExportMessage(message);
        const status = data ? data.status : null;
        this.zone.run(() => {
            switch (status) {
                case "DONE":
                    if (this.element.type == DownloadType.PDF) {
                        this.attemptPdfDownload();
                    } else {
                        this.handleDownload();
                    }
                    break;
                case "ERROR":
                    if (this.element.type == DownloadType.PDF) {
                        this.failPdf();
                    } else {
                        this.element.status = DownloadStatus.ERROR;
                        this.cleanupSocketSubscription();
                    }
                    break;
                default:
                    break;
            }
        });
    }

    private handleDownload(): void {
        if (this.downloadStarted) {
            return;
        }

        this.downloadStarted = true;
        this.downloadService.downloadFile(this.element.uuid, this.element.fileName, this.element.type).then((success) => {
            if (success) {
                this.element.status = DownloadStatus.DOWNLOADED;
                setTimeout(() => this.fadeOut = true, 4000);
            } else {
                this.element.status = DownloadStatus.ERROR;
            }
            this.cleanupSocketSubscription();
        }).catch(err => {
            this.element.status = DownloadStatus.ERROR;
            this.cleanupSocketSubscription();
        });
    }

    private startPdfPolling(): void {
        this.pollPdfTick();
        this.pdfPollIntervalId = setInterval(() => this.pollPdfTick(), PDF_POLL_INTERVAL_MS);
    }

    private pollPdfTick(): void {
        if (this.pdfDownloadSucceeded) {
            this.stopPdfTimers();
            return;
        }
        if (Date.now() - this.pdfStartedAt > PDF_FAIL_DEADLINE_MS) {
            this.failPdf();
            return;
        }
        this.attemptPdfDownload();
    }

    // Shared by STOMP DONE and polling; 404 means "not ready yet" and is retried until the deadline.
    private attemptPdfDownload(): void {
        if (this.pdfDownloadSucceeded || this.pdfDownloadInProgress) {
            return;
        }
        this.pdfDownloadInProgress = true;
        this.downloadService.downloadFile(this.element.uuid, this.element.fileName, this.element.type).then((success) => {
            this.pdfDownloadInProgress = false;
            if (success) {
                this.pdfDownloadSucceeded = true;
                this.element.status = DownloadStatus.DOWNLOADED;
                this.stopPdfTimers();
                this.cleanupSocketSubscription();
                setTimeout(() => this.fadeOut = true, 4000);
            }
        }).catch(() => {
            this.pdfDownloadInProgress = false;
        });
    }

    private failPdf(): void {
        if (this.pdfDownloadSucceeded) {
            return;
        }
        this.element.status = DownloadStatus.ERROR;
        this.stopPdfTimers();
        this.cleanupSocketSubscription();
    }

    private stopPdfTimers(): void {
        if (this.pdfPollStartTimeoutId) {
            clearTimeout(this.pdfPollStartTimeoutId);
            this.pdfPollStartTimeoutId = null;
        }
        if (this.pdfPollIntervalId) {
            clearInterval(this.pdfPollIntervalId);
            this.pdfPollIntervalId = null;
        }
    }

    private parseExportMessage(message: any): any {
        try {
            return JSON.parse(message.body);
        } catch (err) {
            return null;
        }
    }

    private cleanupSocketSubscription(): void {
        if (this.socketSubscriptionId != null) {
            this.socketService.delete(this.socketSubscriptionId);
            this.socketSubscriptionId = null;
        }
    }

    private handleCsvDownload(): void {
        firstValueFrom(this.httpService.getFileWithName(this.element.csvEndpoint, this.element.fileName, this.element.params))
            .then(fileObj => {
                fileObj.fileName = this.element.fileName;
                this.httpUtility.wrapFileAndDownload(fileObj);
                this.element.status = DownloadStatus.DOWNLOADED;
                setTimeout(() => this.fadeOut = true, 4000);
            }).catch(err => {
                this.element.status = DownloadStatus.ERROR;
            });
    }

    private handleCustomDownload(): void {
        this.element.callback().then(() => {
            this.element.status = DownloadStatus.DOWNLOADED;
            setTimeout(() => this.fadeOut = true, 4000);
        }).catch(err => {
            this.element.status = DownloadStatus.ERROR;
        });
    }

    stopDownload(): void {
        this.cleanupSocketSubscription();
        this.stopPdfTimers();
        this.element.status = DownloadStatus.ERROR;
    }

    fadedOut(event: any): void {
        if (event["toState"] == "fading") {
            this.downloadCompleted.emit();
        }
    }
}